import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGroupStore } from '@/stores/groupStore';
import { GroupCard } from '@/components/GroupCard';
import { cn } from '@/lib/utils';
import type { GroupSortType } from '@/types';

type Tab = 'myGroups' | 'browse' | 'invites';

export function GroupsBrowsePage() {
  const {
    activeTab,
    myGroups,
    browseGroups,
    invites,
    searchQuery,
    searchSort,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    setActiveTab,
    loadMyGroups,
    loadMore,
    setSearchQuery,
    setSearchSort,
    joinGroup,
  } = useGroupStore();

  const [localQuery, setLocalQuery] = useState(searchQuery);

  useEffect(() => {
    if (activeTab === 'myGroups' && myGroups.length === 0) loadMyGroups();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(localQuery);
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'myGroups', label: 'My Groups' },
    { key: 'browse', label: 'Browse' },
    { key: 'invites', label: 'Invites' },
  ];

  const sortOptions: { key: GroupSortType; label: string }[] = [
    { key: 'popular', label: 'Popular' },
    { key: 'trending', label: 'Trending' },
    { key: 'newest', label: 'Newest' },
  ];

  const currentItems =
    activeTab === 'myGroups'
      ? myGroups
      : activeTab === 'browse'
        ? browseGroups
        : invites;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !isLoadingMore) {
      loadMore();
    }
  };

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto" onScroll={handleScroll}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold text-card-foreground">Groups</h1>
          <Link
            to="/groups/create"
            className="text-sm px-4 py-2 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Create Group
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={cn(
                'flex-1 py-3 text-sm font-medium transition-colors',
                activeTab === key
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
              {key === 'invites' && invites.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-primary text-primary-foreground">
                  {invites.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search bar (browse tab only) */}
        {activeTab === 'browse' && (
          <div className="px-4 py-3 space-y-3">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Search groups..."
                className="flex-1 px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Search
              </button>
            </form>
            <div className="flex gap-2">
              {sortOptions.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setSearchSort(key)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                    searchSort === key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 text-center">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && currentItems.length === 0 && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && currentItems.length === 0 && (
        <div className="text-center py-12 px-4">
          <GroupsEmptyIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            {activeTab === 'myGroups'
              ? "You haven't joined any groups yet"
              : activeTab === 'invites'
                ? 'No pending invitations'
                : 'No groups found'}
          </p>
          {activeTab === 'myGroups' && (
            <button
              onClick={() => setActiveTab('browse')}
              className="mt-3 text-sm text-primary hover:underline"
            >
              Browse groups
            </button>
          )}
        </div>
      )}

      {/* Group list */}
      <div className="p-4 space-y-3">
        {currentItems.map((group) => (
          <GroupCard
            key={group.id}
            group={group}
            onJoin={(id) => joinGroup(id)}
            onAcceptInvite={(id) => joinGroup(id)}
          />
        ))}
      </div>

      {/* Loading more */}
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      )}
    </div>
  );
}

function GroupsEmptyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  );
}
